import type {
  ComponenteDeServico,
  IcAfetado,
  ServicoAfetado,
  ServicoDoCatalogo,
} from '@/features/changes/tipos';

/**
 * As contas do escopo da mudança — o que ela encosta, e o que a tela oferece.
 *
 * Mora fora do `.tsx` porque as duas decisões daqui têm resposta certa e nenhuma
 * precisa de DOM:
 *
 *   1. **a assinatura da lista**, que faz as vezes de versão. `POST …/services`
 *      e `POST …/cis` substituem a lista inteira e **não** incrementam
 *      `ChangeDetail.version` — o servidor não oferece guarda de concorrência
 *      aqui. A assinatura do conteúdo detecta a mesma corrida sem depender de
 *      uma coluna que não existe: se a lista que veio do servidor mudou depois
 *      de o formulário ter sido semeado, alguém mexeu nela;
 *
 *   2. **o que a lista oferece**. O catálogo é a origem, mas um serviço ou item
 *      já declarado pode ter saído dele. Escondê-lo faria salvar a tela apagar
 *      um vínculo que ninguém pediu para apagar — e, pior, em silêncio: a linha
 *      sumiria da tela e continuaria sendo reenviada, ou deixaria de ser.
 */

/** Nome que a tela dá ao que foi declarado e não está mais no catálogo. */
export const FORA_DO_CATALOGO = 'Fora do catálogo';

/**
 * A assinatura de uma lista de serviços afetados.
 *
 * Ordenada, para que a mesma lista em outra ordem não passe por alteração: a
 * ordem do `findMany` não é contrato, e um falso "alguém mexeu" ensina a
 * ignorar o aviso verdadeiro.
 */
export function assinaturaDeServicos(linhas: readonly ServicoAfetado[]): string {
  return linhas
    .map((linha) =>
      [
        linha.serviceId,
        linha.causesOutage ? 1 : 0,
        linha.expectedDowntimeMinutes ?? '',
        // A nota entra mesmo sem campo na tela: ela é reenviada como veio, e
        // uma alteração dela por outro caminho é alteração da lista.
        linha.note ?? '',
      ].join(':'),
    )
    .sort()
    .join('|');
}

/** A mesma conta para os itens de configuração. */
export function assinaturaDeIcs(linhas: readonly IcAfetado[]): string {
  return linhas
    .map((linha) =>
      [linha.ciId, linha.impactKind, linha.causesOutage ? 1 : 0, linha.note ?? ''].join(':'),
    )
    .sort()
    .join('|');
}

/**
 * Os serviços que a tela oferece: o catálogo, mais o que já foi declarado e
 * saiu dele.
 *
 * O que saiu vai para o fim e vem marcado — a pessoa precisa saber que aquela
 * linha não é mais um serviço vivo do catálogo antes de decidir mantê-la.
 */
export function servicosOferecidos(
  catalogo: readonly ServicoDoCatalogo[],
  declarados: readonly ServicoAfetado[],
): ServicoDoCatalogo[] {
  const oferecidos = [...catalogo];
  const conhecidos = new Set(catalogo.map((servico) => servico.id));
  for (const linha of declarados) {
    if (conhecidos.has(linha.serviceId)) continue;
    conhecidos.add(linha.serviceId);
    oferecidos.push({
      id: linha.serviceId,
      key: linha.service?.key ?? '—',
      name: linha.service?.name ?? FORA_DO_CATALOGO,
      criticality: '—',
    });
  }
  return oferecidos;
}

/**
 * Os itens de configuração que a tela oferece: os componentes dos serviços do
 * catálogo, mais o que já foi declarado e saiu deles.
 *
 * Não há rota que liste o CMDB — o único caminho é
 * `GET /catalog/services/:id/components`, serviço a serviço.
 */
export function icsOferecidos(
  componentes: ReadonlyArray<readonly ComponenteDeServico[] | undefined>,
  declarados: readonly IcAfetado[],
): Array<ComponenteDeServico['ci']> {
  const porId = new Map<string, ComponenteDeServico['ci']>();
  for (const lista of componentes) {
    for (const componente of lista ?? []) porId.set(componente.ci.id, componente.ci);
  }
  for (const linha of declarados) {
    if (porId.has(linha.ciId)) continue;
    porId.set(linha.ciId, {
      id: linha.ciId,
      name: linha.ci?.name ?? FORA_DO_CATALOGO,
      ciType: linha.ci?.ciType ?? '—',
      lifecycleStatus: '—',
    });
  }
  return [...porId.values()];
}
